/*
  Subscription rewards: product panel (box builder in a drawer) + cart drawer behaviour.
  Test theme #191139283268, 2026-09-13.

  Product page: segmented control (Abonnér | Engangskøb), a per-set price and a reward rail;
  "Byg din boks" opens a <dialog> drawer: stacked style cards with length chips (tap = +1,
  badge + minus; several lengths per style), delivery chips, gift unlock strip, total + add. A gift popup shows when a qualifying cart
  is missing the gifts.
  Ladder (box value = cluster subscription lines in the cart + the box being built, full price) — live numbers
  come from the app (window.CosneticLadder, see below); these are the fallback values:
    any -> 10% · 150 kr -> +fri fragt · 400 kr -> 20% + velkomstgave · 600 kr -> 40% + gave (owner's offer in DKK, 2026-09-13)
  One-time: full price, no offer.
  Prices shown are a preview; checkout pricing comes from automatic Shopify discounts
  (OS/brands/COSNETIC/shopify/subrewards-discounts.json) and the Cosnetic Subscription Gift app
  (keep ELIGIBLE / GIFT_VARIANTS / GIFT_PROP in sync with its function).

  Cart updates follow the theme's own pattern: bundle the drawer section into the cart
  request, then dispatch `cart:change` so the drawer re-renders itself.
*/
(() => {
  // ---------------------------------------------------------------- i18n
  // Every string this file prints comes from the theme's locale files, handed over by
  // snippets/js-i18n.liquid as window.CosneticI18n. The second argument is the Danish
  // original, kept as a fallback so the panel still reads correctly if that snippet is
  // ever missing (an app embed reordering the head, a cached page, a partial deploy).
  const I18N = window.CosneticI18n || {};
  const t = (key, fallback, vars) => {
    let out = (I18N.strings && I18N.strings[key]) || fallback || '';
    if (vars) {
      Object.keys(vars).forEach((name) => {
        out = out.replace(new RegExp('\\{\\{\\s*' + name + '\\s*\\}\\}', 'g'), vars[name]);
      });
    }
    return out;
  };
  // Ladder numbers come from the app: shop metafield cosnetic.subscription_ladder, printed as
  // window.CosneticLadder in layout/theme.liquid and rebuilt from the live Shopify discounts and the gift
  // settings by the app's Kampagner page. The literals below are only the fallback when that data is missing.
  // The ladder's numbers (giftMin, steps, shippingBox...) are amounts in the STORE's
  // currency. Before this, they were applied verbatim on every market, so on cosnetic.com
  // the Danish "400" was compared against a USD cart and the welcome gift only unlocked at
  // $400 instead of the intended ~$64.
  //
  // markets.<handle> in the metafield now holds that market's own amounts; anything it does
  // not specify falls through to the Danish base, so cosnetic.dk is unaffected. Product and
  // gift variant ids are shared and always come from the base.
  const RAW_LADDER = window.CosneticLadder || {};
  const MARKET = (window.CosneticI18n && window.CosneticI18n.market) || null;
  const LADDER = (() => {
    const override = MARKET && RAW_LADDER.markets && RAW_LADDER.markets[MARKET];
    if (!override) return RAW_LADDER;
    const merged = Object.assign({}, RAW_LADDER, override);
    ['lashes', 'nails'].forEach((kind) => {
      merged[kind] = Object.assign({}, RAW_LADDER[kind] || {}, override[kind] || {});
    });
    return merged;
  })();
  const MAX_PER_LENGTH = 20;
  const GIFT_PROP = '_subrewards_gift';
  const ore = (kr) => Math.round(Number(kr) * 100);
  const idList = (list, fallback) => (Array.isArray(list) && list.length ? list.map(Number) : fallback);
  const SHIP_BOX = LADDER.shippingBox != null ? Number(LADDER.shippingBox) : 2;

  // Rail stops = every discount step plus the free-shipping stop, with the % in force at each stop.
  const buildRule = (key, fallback) => {
    const data = LADDER[key] || {};
    const steps = (Array.isArray(data.steps) && data.steps.length ? data.steps : fallback.steps).map((step) => ({
      min: Number(step.min), pct: Number(step.percent), giftIds: Array.isArray(step.giftVariantIds) ? step.giftVariantIds.map(Number) : null,
    }));
    // A step may carry its own gifts (owner 2026-11-03: Bond & Seal at 4 sets, all three at 6). Then the gift level is
    // the highest such step the box reaches; without them every box from giftMin gets the full gift list.
    const giftTiers = data.giftEnabled === false ? [] : steps.filter((step) => step.giftIds && step.giftIds.length).map((step) => ({ min: step.min, ids: step.giftIds }));
    const giftMin = data.giftEnabled === false ? Infinity : giftTiers.length ? giftTiers[0].min : data.giftMin != null ? Number(data.giftMin) : 6;
    const stops = steps.map((step) => ({ ...step, ship: SHIP_BOX > 0 && step.min === SHIP_BOX }));
    if (SHIP_BOX > 0 && !stops.some((stop) => stop.ship)) stops.push({ min: SHIP_BOX, pct: null, ship: true });
    stops.sort((a, b) => a.min - b.min);
    let pct = steps[0].pct;
    stops.forEach((stop) => { if (stop.pct == null) stop.pct = pct; else pct = stop.pct; });
    return {
      eligible: new Set(idList(data.productIds, fallback.eligible)),
      gifts: idList(data.giftVariantIds, fallback.gifts),
      giftMode: fallback.giftMode,
      giftMin,
      giftTiers,
      tiers: stops.map((stop) => stop.min),
      pcts: stops.map((stop) => stop.pct),
      labels: stops.slice(1).map((stop) => {
        if (stop.ship && !steps.some((step) => step.min === stop.min)) return t('cosnetic.js.free_shipping_lower', 'fri fragt');
        const tierGift = giftTiers.find((g) => g.min === stop.min);
        const giftWord = tierGift && tierGift.ids.length === 1 ? t('cosnetic.js.gift_label_one', 'gratis Bond & Seal') : fallback.giftLabel;
        let label = (giftTiers.length ? !!tierGift : stop.min === giftMin)
          ? t('cosnetic.js.tier_plus_gift', '{{ percent }}% rabat + {{ gift }}', { percent: Math.round(stop.pct), gift: giftWord })
          : t('cosnetic.js.tier_percent', '{{ percent }}% rabat', { percent: Math.round(stop.pct) });
        if (stop.ship) label = t('cosnetic.js.tier_plus_shipping', '{{ label }} + fri fragt', { label });
        return label;
      }),
    };
  };
  const FALLBACK_STEPS = [{ min: 0, percent: 10 }, { min: 6, percent: 20 }, { min: 8, percent: 50 }];
  // Lashes and nails each have their own box. Nails: ONE free set of their choice.
  const RULES = {
    lashes: buildRule('lashes', {
      steps: FALLBACK_STEPS,
      eligible: [15532471484740, 15532470763844, 15532470960452, 15532470337860, 8543196053828, 8543196283204, 9086849352004], // clusters + accessories on subscription
      gifts: [46730034020676, 46730036543812, 34801156980869],
      giftMode: 'each',
      giftLabel: t('cosnetic.js.gift_label_lashes', '3 gratis gaver'),
    }),
    nails: buildRule('nails', {
      steps: FALLBACK_STEPS,
      eligible: [15182808219972, 15182813266244, 15182815461700, 15182816969028],
      gifts: [55209233383748, 55209247834436, 55209254453572, 55209257861444],
      giftMode: 'choose',
      giftLabel: t('cosnetic.js.gift_label_nails', 'gratis neglesæt'),
    }),
  };
  const TOP_PCT = Math.max(...RULES.lashes.pcts, ...RULES.nails.pcts);
  const GIFT_DISMISS_KEY = 'subrewards-gift-popup-dismissed';
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Shop money format has no decimals, so format øre by hand (67,50 kr, not 68 kr).
  // One-time = full price (owner 2026-10-14: "remove the 3-for-2"; the automatic BXGY discounts
  // "Køb 3, betal for 2" + "– negle" were ended at the same time). Only the box carries a deal.
  const oneTimeTotal = (unitPrices) => unitPrices.reduce((sum, price) => sum + price, 0);
  // Split test tre_for_to (2026-10-21, owner: "split teste 3 for 2 vs ingenting"): arm B keeps "Køb 3, betal for 2" on
  // one-time purchases. snippets/cosnetic-ab sets the class on <html>; the discount itself comes from the gift app
  // ("Køb 3, betal for 2 (vipper)/(negle)") and only applies to carts tagged with arm B. Of every three sets the
  // cheapest is free, like the old native BXGY. Arm A and everyone outside the test: full price.
  const offer3 = () => document.documentElement.classList.contains('cx-tre_for_to-b');
  const threeForTwo = (unitPrices) => {
    const sorted = [...unitPrices].sort((a, b) => a - b);
    const free = sorted.slice(0, Math.floor(sorted.length / 3)).reduce((sum, price) => sum + price, 0);
    return sorted.reduce((sum, price) => sum + price, 0) - free;
  };
  const threeForTwoHint = (sets, kind = 'lashes') => {
    const free = Math.floor(sets / 3);
    const rest = sets % 3;
    if (!sets) return kind === 'nails'
      ? t('cosnetic.subscription.bogo_hint_nails', 'Køb 3, betal for 2 – mix designs frit')
      : t('cosnetic.subscription.bogo_hint', 'Køb 3, betal for 2 – mix længder frit');
    if (rest === 2) return t('cosnetic.js.bogo_add_one', 'Tilføj 1 sæt mere → det er gratis');
    if (rest === 1) return free
      ? t('cosnetic.js.bogo_free_plus', '{{ count }} gratis ✓ · tilføj 2 mere → 1 gratis til', { count: free })
      : t('cosnetic.js.bogo_add_two', 'Tilføj 2 sæt mere → det 3. er gratis');
    return t('cosnetic.js.bogo_sets_free', '{{ count }} sæt gratis ✓', { count: free });
  };
  const oneTimePrice = (unitPrices) => (offer3() ? threeForTwo(unitPrices) : oneTimeTotal(unitPrices));

  // Was hardcoded to da-DK + " kr", which printed Danish kroner formatting for every
  // market -- USD amounts on cosnetic.com came out as "12,00 kr". The Danish branch is the
  // original code, kept verbatim so cosnetic.dk renders exactly as it did; every other
  // market formats in its own currency.
  const MONEY_CURRENCY = (I18N.money && I18N.money.currency) || 'DKK';
  const MONEY_IS_DANISH = !I18N.money || I18N.money.isDanish !== false;
  const intlMoney = (() => {
    try {
      // No explicit fraction digits: each currency uses its own convention, so USD
      // renders $43.20 rather than the $43.2 that minimumFractionDigits: 0 produced.
      return new Intl.NumberFormat(I18N.locale || 'en', {
        style: 'currency',
        currency: MONEY_CURRENCY,
      });
    } catch (e) {
      return null;
    }
  })();
  const formatMoney = (cents) => {
    const value = Math.max(0, Math.round(cents));
    if (MONEY_IS_DANISH || !intlMoney) {
      const kr = Math.floor(value / 100);
      const ore = value % 100;
      return `${kr.toLocaleString('da-DK')}${ore ? ',' + String(ore).padStart(2, '0') : ''} kr`;
    }
    return intlMoney.format(value / 100);
  };
  // Box prices rounded per currency (owner 2026-11-03: "always round discounts, prices, etc per country"). The ladder's
  // `rounding` map gives each currency's step (kr 1, €/$/£ 0.50): a unit is the tier % off to whole øre/cents, then down
  // to the step — the same sum the gift app's "box" discount charges. No map = the plain % as before.
  const ROUND_STEP = (() => {
    const map = LADDER.rounding;
    if (!map || typeof map !== 'object') return 0;
    const step = Number(map[MONEY_CURRENCY] != null ? map[MONEY_CURRENCY] : map.default);
    return step > 0 ? Math.round(step * 100) : 0;
  })();
  const boxUnit = (unit, pct) => {
    const exact = Math.round((unit * (100 - pct)) / 100);
    return ROUND_STEP && pct > 0 ? Math.floor(exact / ROUND_STEP) * ROUND_STEP : exact;
  };
  const boxTotal = (full, pct, n) => (ROUND_STEP && pct > 0 && n > 0 ? boxUnit(full / n, pct) * n : Math.round((full * (100 - pct)) / 100));


  const percentFor = (box, rule = RULES.lashes) => rule.tiers.reduce((pct, min, i) => (box >= min ? rule.pcts[i] : pct), rule.pcts[0]);

  // Fill (0-100) of a reward rail whose stops sit evenly spaced along the bar.
  const railProgress = (value, stops) => {
    const last = stops.length - 1;
    if (value >= stops[last]) return 100;
    if (value < stops[0]) return 0;
    for (let i = 0; i < last; i++) {
      if (value < stops[i + 1]) return ((i + (value - stops[i]) / (stops[i + 1] - stops[i])) / last) * 100;
    }
    return 100;
  };

  const nextStepFor = (box, rule = RULES.lashes) => {
    for (let i = 1; i < rule.tiers.length; i++) {
      if (box < rule.tiers[i]) return { amount: rule.tiers[i] - box, label: rule.labels[i - 1] };
    }
    return null;
  };

  const boxValue = (cart, rule = RULES.lashes) =>
    cart.items.reduce(
      (sum, item) => (item.selling_plan_allocation && rule.eligible.has(item.product_id) ? sum + item.quantity : sum),
      0
    );

  // Full-price value of those same lines (the box line of split-test arm C shows what the box is worth).
  const boxFull = (cart, rule = RULES.lashes) =>
    cart.items.reduce(
      (sum, item) => (item.selling_plan_allocation && rule.eligible.has(item.product_id) ? sum + item.original_line_price : sum),
      0
    );

  const isGift = (item, rule = RULES.lashes) => !!(item.properties && item.properties[GIFT_PROP] && rule.gifts.includes(item.variant_id));

  // The gifts a box of this size earns (per-step gift tiers, else the full list from giftMin).
  const giftsFor = (box, rule = RULES.lashes) => {
    if (rule.giftTiers && rule.giftTiers.length) {
      const tier = rule.giftTiers.filter((g) => box >= g.min).pop();
      return tier ? tier.ids : [];
    }
    return box >= rule.giftMin ? rule.gifts : [];
  };

  const missingGifts = (cart, rule = RULES.lashes, chosen = null, box = null) => {
    const present = cart.items.filter((item) => isGift(item, rule));
    const line = (id) => ({ id, quantity: 1, properties: { [GIFT_PROP]: '1' } });
    if (rule.giftMode === 'choose') return present.length ? [] : [line(chosen || rule.gifts[0])];
    const have = new Set(present.map((item) => item.variant_id));
    return giftsFor(box == null ? boxValue(cart, rule) : box, rule).filter((id) => !have.has(id)).map(line);
  };

  const storage = {
    get(key) {
      try { return window.sessionStorage.getItem(key); } catch (error) { return null; }
    },
    set(key, value) {
      try { window.sessionStorage.setItem(key, value); } catch (error) { /* private mode */ }
    },
  };

  let lastCart = null;

  const fetchCart = async () => (await fetch(`${ROOT}cart.js`)).json();

  const bundledSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  };

  // `variant:add` as baseEvent makes the drawer swap its content immediately (no 1.25s delay).
  const announce = (cart) =>
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } })
    );

  const postJSON = async (path, body) => {
    const response = await fetch(`${ROOT}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    const json = await response.json();
    if (!response.ok) throw new Error(json.description || json.message || t('cosnetic.js.generic_error', 'Der opstod en fejl. Prøv igen.'));
    return json;
  };

  // `beforeAnnounce` runs once the items are in the cart, before the theme opens its cart drawer.
  const addItems = async (items, source, beforeAnnounce) => {
    // V3 (Horizon): the drawer only reacts to CartLinesUpdateEvent -- see assets/cosnetic-ui.js.
    if (window.CosneticHorizonCart) {
      if (beforeAnnounce) beforeAnnounce();
      return window.CosneticHorizonCart.add(items, source || undefined);
    }
    const added = await postJSON('cart/add.js', { items, sections: bundledSections().join(',') });
    const cart = await fetchCart();
    cart.sections = added.sections;
    if (beforeAnnounce) beforeAnnounce();
    if (source) {
      source.dispatchEvent(
        new CustomEvent('variant:add', { bubbles: true, detail: { items: added.items || [added], cart } })
      );
    }
    announce(cart);
    return cart;
  };

  const addMissingGifts = async (rule = RULES.lashes, chosen = null, beforeAnnounce) => {
    const items = missingGifts(await fetchCart(), rule, chosen);
    if (items.length) await addItems(items, null, beforeAnnounce);
    else if (beforeAnnounce) beforeAnnounce();
  };

  const changeLine = async (body) => {
    if (window.CosneticHorizonCart) return window.CosneticHorizonCart.change(body);
    const cart = await postJSON('cart/change.js', { ...body, sections: bundledSections() });
    announce(cart);
    return cart;
  };

  const updateLines = async (updates) => {
    const cart = await postJSON('cart/update.js', { updates, sections: bundledSections() });
    announce(cart);
    return cart;
  };

  // Gifts only belong in a cart whose box (per rule) reaches the gift step: lashes one of each,
  // nails a single chosen set.
  let syncingGifts = false;
  const syncGifts = async (cart) => {
    if (syncingGifts || !cart || !cart.items) return;
    const updates = {};
    Object.values(RULES).forEach((rule) => {
      const gifts = cart.items.filter((item) => isGift(item, rule));
      if (!gifts.length) return;
      const box = boxValue(cart, rule);
      const seen = new Set();
      const allowed = rule.giftMode === 'choose' ? null : new Set(giftsFor(box, rule));
      gifts.forEach((item, index) => {
        const extra = rule.giftMode === 'choose' ? index > 0 : seen.has(item.variant_id);
        if (box < rule.giftMin || extra || (allowed && !allowed.has(item.variant_id))) updates[item.key] = 0;
        else if (item.quantity > 1) updates[item.key] = 1;
        seen.add(item.variant_id);
      });
    });
    if (!Object.keys(updates).length) return;

    syncingGifts = true;
    try {
      await updateLines(updates);
    } catch (error) {
      console.error('[subrewards]', error);
    } finally {
      syncingGifts = false;
    }
  };

  const refreshPanels = (cart, fromChange) =>
    document.querySelectorAll('subrewards-panel').forEach((panel) => panel.setCart && panel.setCart(cart, fromChange));

  document.addEventListener('cart:change', (event) => {
    const cart = event.detail && event.detail.cart;
    if (!cart || !cart.items) return;
    lastCart = cart;
    refreshPanels(cart, true);
    syncGifts(cart);
  });

  // Cart drawer: add the welcome gift
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-subrewards-add-gifts]');
    if (!button) return;
    event.preventDefault();
    button.setAttribute('aria-busy', 'true');
    try {
      await addMissingGifts(RULES[button.dataset.kind] || RULES.lashes, Number(button.dataset.variantId) || null);
    } catch (error) {
      console.error('[subrewards]', error);
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      button.removeAttribute('aria-busy');
    }
  });

  // Cart drawer: change delivery interval on a subscription line
  document.addEventListener('change', async (event) => {
    const select = event.target.closest('select[data-subrewards-line-plan]');
    if (!select) return;
    select.disabled = true;
    try {
      await changeLine({
        id: select.dataset.lineKey,
        quantity: parseInt(select.dataset.quantity, 10),
        selling_plan: parseInt(select.value, 10),
      });
    } catch (error) {
      console.error('[subrewards]', error);
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  });

  const setRadio = (buttons, isSelected, attribute = 'aria-checked') =>
    buttons.forEach((button) => {
      const selected = isSelected(button);
      button.classList.toggle(attribute === 'aria-pressed' ? 'is-active' : 'is-selected', selected);
      button.setAttribute(attribute, String(selected));
    });

  class SubrewardsPanel extends HTMLElement {
    connectedCallback() {
      this.productId = Number(this.dataset.productId);
      this.variantId = Number(this.dataset.variantId);
      this.price = Number(this.dataset.price);
      this.mode = this.dataset.mode || 'subscription';
      this.cartBox = 0;
      this.touched = false;
      this.rule = RULES[this.dataset.kind] || RULES.lashes;
      this.returning = this.dataset.returning === 'true';
      if (this.returning) this.querySelectorAll('[data-sr-gifts], [data-sr-gift-teaser], [data-sr-c-gift]').forEach((el) => el.remove());
      this.onceQty = 1;

      // Split test "buybox" arm C: one length + a quantity, added straight to the cart.
      this.c = this.querySelector('[data-sr-c]');
      this.cQty = 1;
      this.cVariant = this.variantId;
      this.cPrice = this.price;
      this.cAvailable = this.dataset.available !== 'false';
      this.cartFull = 0;

      // Box size picker (owner 2026-11-03, "simplify build a box"): pick 2/6/8 sets, then fill that many with lengths.
      this.s = this.querySelector('[data-sr-s]');
      if (this.s && this.s.hasAttribute('data-prefill')) this._spreadS(Number(this.s.dataset.size));

      this.drawer = this.querySelector('[data-sr-drawer]');
      this.giftModal = this.querySelector('[data-sr-gift-modal]');
      this.planSelect = this.querySelector('[data-subrewards-plan]');
      this.cta = this.querySelector('[data-subrewards-add]');
      this.ctaLabel = this.querySelector('[data-sr-cta-label]');
      this.errorEl = this.querySelector('[data-subrewards-error]');

      this.querySelectorAll('[data-sr-money]').forEach((el) => {
        el.textContent = formatMoney(Number(el.dataset.srMoney));
      });

      this._onClick = this._onClick.bind(this);
      this._onBackdrop = (event) => {
        if (event.target === event.currentTarget) event.currentTarget.close();
      };
      this._onVariantChange = (event) => {
        const detail = event.detail;
        if (!detail || !detail.variant || !detail.product || Number(detail.product.id) !== this.productId) return;
        this.variantId = Number(detail.variant.id);
        this.price = Number(detail.variant.price);
        // Until the shopper touches the builder, the box follows the variant picked on the page.
        if (!this.touched) {
          const len = this._lenFor(this.variantId);
          if (len && !len.classList.contains('is-soldout')) {
            this.lens.forEach((el) => this._setCount(el, el === len ? 1 : 0));
          }
        }
        this.render();
      };

      this.addEventListener('click', this._onClick);

      // #build (optionally #build-<n>) opens the drawer with a preset box applied. The
      // homepage starter-kit hero links here, which is the whole "one click to order" path.
      if (/^#build(-\d+)?$/.test(window.location.hash)) {
        const sets = Number(window.location.hash.split('-')[1]) || 8;
        requestAnimationFrame(() => {
          if (this.s) {
            const sizes = [...this.s.querySelectorAll('[data-sr-s-size]')].map((b) => Number(b.dataset.srSSize));
            this._pickSize(sizes.find((n) => n >= sets) || sizes[sizes.length - 1]);
            this.render();
            this.s.scrollIntoView({ block: 'start', behavior: 'smooth' });
            return;
          }
          const preset = this.querySelector('[data-sr-preset]');
          if (preset) {
            preset.dataset.srPreset = String(sets);
            preset.click();
          }
          this.openDrawer();
        });
      }
      [this.drawer, this.giftModal].forEach((dialog) => dialog && dialog.addEventListener('click', this._onBackdrop));
      document.addEventListener('variant:change', this._onVariantChange);

      this.render();
      fetchCart()
        .then((cart) => {
          lastCart = cart;
          this.setCart(cart, false);
        })
        .catch(() => {});
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      document.removeEventListener('variant:change', this._onVariantChange);
    }

    get lens() {
      return [...this.querySelectorAll('[data-sr-len]')];
    }

    get picked() {
      return this.lens
        .map((el) => ({
          variantId: Number(el.dataset.variantId),
          productId: Number(el.dataset.productId),
          price: Number(el.dataset.price),
          count: Number(el.dataset.count) || 0,
        }))
        .filter((item) => item.count > 0);
    }

    // Accessories added to the lash subscription: full price, don't count toward the box/tiers.
    get addons() {
      return [...this.querySelectorAll('[data-sr-addon]')]
        .map((el) => ({ el, variantId: Number(el.dataset.variantId), price: Number(el.dataset.price), count: Number(el.dataset.count) || 0 }))
        .filter((item) => item.count > 0);
    }

    // Accessories added to a one-time purchase on the product page (full price).
    get onceAddons() {
      return [...this.querySelectorAll('[data-sr-once-addon]')]
        .map((el) => ({ variantId: Number(el.dataset.variantId), price: Number(el.dataset.price), count: Number(el.dataset.count) || 0 }))
        .filter((item) => item.count > 0);
    }

    _lenFor(variantId) {
      return this.lens.find((el) => Number(el.dataset.variantId) === variantId);
    }

    _setCount(len, count) {
      const next = Math.min(MAX_PER_LENGTH, Math.max(0, count));
      len.dataset.count = String(next);
      len.classList.toggle('is-picked', next > 0);
      const label = len.querySelector('[data-sr-len-count]');
      if (label) label.textContent = String(next);
    }

    setCart(cart, fromChange) {
      this.cartBox = boxValue(cart, this.rule);
      this.cartFull = boxFull(cart, this.rule);
      this.render();
      if (fromChange) this._maybeShowGiftPopup(cart);
    }

    _maybeShowGiftPopup(cart) {
      if (this.returning) return;
      if (!this.giftModal || this.giftModal.open || this.adding) return;
      if (this.drawer && this.drawer.open) return;
      if (storage.get(GIFT_DISMISS_KEY)) return;
      if (boxValue(cart, this.rule) < this.rule.giftMin || !missingGifts(cart, this.rule, this._chosenGift()).length) return;
      this.giftModal.showModal();
    }

    openDrawer() {
      if (this.drawer && !this.drawer.open) this.drawer.showModal();
      this.render();
    }

    closeDrawer() {
      if (this.drawer && this.drawer.open) this.drawer.close();
    }

    _setText(selector, text) {
      this.querySelectorAll(selector).forEach((el) => {
        el.textContent = text;
      });
    }

    _setHidden(selector, hidden) {
      this.querySelectorAll(selector).forEach((el) => {
        el.hidden = hidden;
      });
    }

    render() {
      const subscribing = this.mode === 'subscription';
      const picked = this.picked;
      const sets = picked.reduce((sum, item) => sum + item.count, 0);
      const value = picked.reduce((sum, item) => sum + item.count * item.price, 0);
      const unit = this.price || 7500;

      this.dataset.mode = this.mode;
      this.querySelectorAll('[data-sr-show]').forEach((el) => {
        el.hidden = el.dataset.srShow !== this.mode;
      });
      setRadio(this.querySelectorAll('[data-sr-mode]'), (tab) => tab.dataset.srMode === this.mode);

      // style cards: "2 valgt"
      this.querySelectorAll('[data-sr-style]').forEach((card) => {
        const count = picked
          .filter((item) => item.productId === Number(card.dataset.productId))
          .reduce((sum, item) => sum + item.count, 0);
        card.classList.toggle('is-picked', count > 0);
        const badge = card.querySelector('[data-sr-style-count]');
        badge.hidden = count === 0;
        badge.textContent = t('cosnetic.js.count_selected', '{{ count }} valgt', { count });
      });

      let now;
      let perSet;
      let next;
      let box = 0;
      if (subscribing) {
        // accessories on subscription count in the box and get the same tier %
        const accessoryValue = this.addons.reduce((sum, item) => sum + item.count * item.price, 0);
        const accessoryCount = this.addons.reduce((sum, item) => sum + item.count, 0);
        box = this.cartBox + sets + accessoryCount;
        const pct = percentFor(box, this.rule);
        now = Math.round(((value + accessoryValue) * (100 - pct)) / 100);
        perSet = boxUnit(unit, pct);
  
        const step = nextStepFor(box, this.rule);
        next = step
          ? t('cosnetic.js.add_sets_more', 'Tilføj {{ count }} sæt mere → {{ label }}', { count: Math.max(1, step.amount), label: step.label })
          : 'Du har den laveste pris ✓';

        const unlocked = box >= this.rule.giftMin;
        const gifts = this.querySelector('[data-sr-gifts]');
        if (gifts) gifts.classList.toggle('is-unlocked', unlocked);
        this._setText('[data-sr-gift-tag]', unlocked ? t('cosnetic.subscription.free', 'Gratis') : t('cosnetic.subscription.locked', 'Låst'));
        this._setText(
          '[data-sr-gift-status]',
          unlocked
            ? t('cosnetic.js.gift_unlocked_auto', 'Låst op ✓ – lægges automatisk i kurven')
            : t('cosnetic.js.add_sets_to_unlock', 'Tilføj {{ count }} sæt mere for at låse op', { count: Math.max(1, this.rule.giftMin - box) })  // box and giftMin are SET COUNTS since the ladder moved from kr; dividing by the per-set
            // price in ore (the old kr logic) made this always read 1
        );
      } else {
        // one-time: full price, no offer (Martin 2026-09-13, again 2026-10-14)
        now = oneTimePrice(picked.flatMap((item) => Array(item.count).fill(item.price)));
        perSet = sets ? Math.round(now / sets) : unit;
        next = offer3() ? threeForTwoHint(sets, this.dataset.kind) : '';
      }

      // reward rails
      this.querySelectorAll('[data-sr-rail]').forEach((rail) => {
        const railValue = rail.dataset.srRail === 'subscription' ? box : sets;
        const stops = [...rail.querySelectorAll('[data-sr-stop]')];
        const values = stops.map((stop) => Number(stop.dataset.srStop));
        stops.forEach((stop, i) => stop.classList.toggle('is-done', railValue >= values[i]));
        rail.querySelector('[data-sr-rail-fill]').style.width = `${railProgress(railValue, values)}%`;
      });

      // product page card: headline is always the cheapest price the offer reaches
      // (subscription top tier; one-time = the full price); the line below shows what they pay now.
      const best = subscribing
        ? { price: boxUnit(unit, this.rule.pcts[this.rule.pcts.length - 1]), reached: box >= this.rule.tiers[this.rule.tiers.length - 1] }
        : offer3()
          ? { price: Math.ceil((unit * 2) / 3 / 100) * 100, reached: false } // whole kr, rounded up
          : { price: unit, reached: true };
      this._setText('[data-sr-gift-min]', t('cosnetic.js.sets', '{{ sets }} sæt', { sets: this.rule.giftMin }));
      this._setText('[data-sr-per-set]', formatMoney(best.price));
      this._setHidden('[data-sr-from]', best.reached);
      this._setText('[data-sr-per-set-was]', formatMoney(unit));
      const topPct = this.rule.pcts[this.rule.pcts.length - 1];
      this._setText('[data-sr-save-tag]', subscribing
        ? t('cosnetic.product_card.sub_save_up_to', 'Spar op til {{ percent }}%', { percent: topPct })
        : offer3() ? t('cosnetic.js.bogo_tag', 'Køb 3, betal for 2') : '');
      this._setHidden('[data-sr-save-tag]', !subscribing && !offer3());
      this._setText('[data-sr-best]', best.reached
        ? t('cosnetic.js.lowest_price', 'Laveste pris ✓')
        : t('cosnetic.js.now_per_set', 'Nu {{ price }}/sæt', { price: formatMoney(perSet) }));
      this._setHidden('[data-sr-best]', !subscribing);
      this._setHidden('[data-sr-per-set-was]', !subscribing && !offer3());
      // Only products with lengths get the length hint; Bond & Seal, serums and nails have one variant
      // and used to be told to pick a length that does not exist.
      const hasLengths = Number(this.dataset.lengths || 2) > 1;
      this._setText('[data-sr-next]', sets ? next : subscribing
        ? (hasLengths ? t('cosnetic.js.tap_length_first', 'Tryk på en længde for at tilføje dit første sæt')
                      : t('cosnetic.js.tap_plus_first', 'Tryk + for at tilføje den til din boks'))
        : '');

      // drawer footer
      const addons = subscribing ? this.addons : [];
      const addonCount = addons.reduce((sum, item) => sum + item.count, 0);
      const addonValue = addons.reduce((sum, item) => sum + item.count * item.price, 0);
      this._setText('[data-sr-count]', addonCount
        ? t('cosnetic.js.sets_plus_addons', '{{ sets }} sæt + {{ addons }} tilbehør', { sets, addons: addonCount })
        : t('cosnetic.js.sets', '{{ sets }} sæt', { sets }));
      this._setText('[data-sr-total-now]', formatMoney(now));
      this._setText('[data-sr-total-was]', formatMoney(value + addonValue));
      this._setHidden('[data-sr-total-was]', value + addonValue === now);
      this._setText('[data-sr-save-amount]', t('cosnetic.js.you_save', 'Du sparer {{ amount }}', { amount: formatMoney(value + addonValue - now) }));
      this._setHidden('[data-sr-save-amount]', value + addonValue - now <= 0);

      // product page one-time: length chips + quantity, button adds straight to cart
      // product page one-time: a counter per length (starts at 0) + accessories; greyed out until something is added
      const onceEls = [...this.querySelectorAll('[data-sr-once-addon]')];
      const onceSetUnits = onceEls.filter((el) => el.closest('.sr-once__len')).flatMap((el) => Array(Number(el.dataset.count) || 0).fill(Number(el.dataset.price)));
      const onceFull = this.onceAddons.reduce((sum, item) => sum + item.count * item.price, 0);
      const onceSetsFull = onceSetUnits.reduce((sum, price) => sum + price, 0);
      const onceTotal = onceFull - (onceSetsFull - oneTimePrice(onceSetUnits));
      if (offer3()) this._setText('[data-sr-once-hint]', threeForTwoHint(onceSetUnits.length, this.dataset.kind));
      this._setHidden('[data-sr-once-hint]', !offer3());
      const pdpCta = this.querySelector('[data-sr-open]');
      if (pdpCta) {
        pdpCta.disabled = !subscribing && onceTotal === 0;
        this._setText('[data-sr-pdp-cta-label]', subscribing
          ? t('cosnetic.subscription.build_your_box', 'Byg din boks')
          : onceTotal
            ? t('cosnetic.js.add_to_cart_total', 'Tilføj til kurv · {{ total }}', { total: formatMoney(onceTotal) })
            : t('cosnetic.js.add_to_cart', 'Tilføj til kurv'));
      }

      this.cta.disabled = sets === 0;
      this.ctaLabel.textContent = sets === 0
        ? t('cosnetic.js.choose_min_one', 'Vælg mindst 1 sæt')
        : subscribing
          ? t('cosnetic.subscription.add_box_to_cart', 'Tilføj boks til kurv')
          : t('cosnetic.js.put_in_cart', 'Læg i kurv');

      if (this.c) this._renderC(subscribing);
      if (this.s) this._renderS();
    }

    get sLens() {
      return [...this.s.querySelectorAll('[data-sr-s-len]')];
    }

    _setS(el, count) {
      el.dataset.count = String(count);
      el.classList.toggle('is-picked', count > 0);
      el.querySelector('[data-sr-s-n]').textContent = String(count);
    }

    // Choosing a size refills the rows: spread over this product's lengths (nails: this design), like the old preset.
    _spreadS(size) {
      this.s.dataset.size = String(size);
      const lens = this.sLens;
      lens.forEach((el) => this._setS(el, 0));
      const sets = lens.filter((el) => !el.hasAttribute('data-addon'));
      const own = sets.filter((el) => Number(el.dataset.productId) === this.productId);
      const all = own.length ? own : sets;
      // Prefer lengths that ship today (pre-order lengths with no stock wait for the delivery), as long as their
      // stock covers the box; otherwise spread over every length.
      const stock = (el) => (el.dataset.preStock != null ? Number(el.dataset.preStock) : Infinity);
      const ready = all.filter((el) => stock(el) > 0);
      const spread = (pool) => {
        pool.forEach((el) => this._setS(el, 0));
        for (let i = 0; i < size && pool.length; i++) {
          const el = pool[i % pool.length];
          this._setS(el, Number(el.dataset.count) + 1);
        }
        return pool.every((el) => Number(el.dataset.count) <= stock(el));
      };
      if (!ready.length || !spread(ready)) spread(all);
    }

    // Lashes (nothing pre-selected): the card she taps is her target; nails refill the rows like before.
    _pickSize(size) {
      if (this.s.hasAttribute('data-prefill')) this._spreadS(size);
      else this.s.dataset.size = String(size);
    }

    // what a box of n sets costs at its tier, at a given average set price
    _boxCost(n, unit) {
      return boxTotal(n * unit, percentFor(n, this.rule), n);
    }

    _renderS() {
      const s = this.s;
      const lens = this.sLens;
      const picked = lens.reduce((sum, el) => sum + Number(el.dataset.count), 0);
      const full = lens.reduce((sum, el) => sum + Number(el.dataset.count) * Number(el.dataset.price), 0);
      // owner 2026-11-03: "you should be able to add as many lashes and products as you want, not limited to 8".
      // The chosen card is the target: the button asks for that many, she can go past it, and the card moves up with
      // her when she reaches a bigger one. Accessories count in the box like on the old drawer (same tier %).
      const pct = percentFor(picked, this.rule);
      const sizes = [...s.querySelectorAll('[data-sr-s-size]')].map((b) => Number(b.dataset.srSSize));
      const reached = sizes.filter((n) => n <= picked).pop() || 0;
      if (reached > Number(s.dataset.size)) s.dataset.size = String(reached);
      const target = Number(s.dataset.size);
      setRadio(s.querySelectorAll('[data-sr-s-size]'), (b) => Number(b.dataset.srSSize) === target);
      s.querySelectorAll('[data-sr-s-opt]').forEach((opt) => opt.classList.toggle('is-selected', Number(opt.dataset.srSOpt) === target));
      // owner 2026-11-03: "make the varaint picker a part of the chosen buy box" — the picker moves into the chosen card
      const chosenCard = s.querySelector('[data-sr-s-opt].is-selected');
      const lensBlock = s.querySelector('.sr-s__lens');
      if (chosenCard && lensBlock && lensBlock.parentElement !== chosenCard) chosenCard.append(lensBlock);

      const step = nextStepFor(picked, this.rule);
      s.querySelector('[data-sr-s-count]').textContent = picked < target
        ? s.dataset.labelPicked.replace('%P', picked).replace('%N', target)
        : s.dataset.labelInBox.replace('%C', picked)
          + (step ? ` · ${t('cosnetic.js.add_sets_more', 'Tilføj {{ count }} sæt mere → {{ label }}', { count: step.amount, label: step.label })}`
            // 6-7 sets cost more than 8 on this ladder: say so, it is the reason to add them
            + (this._boxCost(picked + step.amount, full / Math.max(1, picked)) <= this._boxCost(picked, full / Math.max(1, picked)) ? `, ${s.dataset.labelCheaper}` : '') : '');
      // the folded summary (nails): "French Originals × 5"
      const sum = s.querySelector('[data-sr-s-sum]');
      if (sum) sum.textContent = lens
        .filter((el) => Number(el.dataset.count) > 0)
        .map((el) => `${el.dataset.name} × ${el.dataset.count}`)
        .join(' · ');
      lens.forEach((el) => {
        el.querySelector('[data-sr-s-step="1"]').disabled = Number(el.dataset.count) >= MAX_PER_LENGTH;
        el.querySelector('[data-sr-s-step="-1"]').disabled = Number(el.dataset.count) === 0;
      });

      // A pre-order length in the box (snippets/cc-preorder) means the box ships with that delivery, not today.
      const ship = s.querySelector('[data-sr-s-ship]');
      if (ship && ship.dataset.pre) {
        const pre = lens.some((el) => el.dataset.preStock != null && Number(el.dataset.count) > Number(el.dataset.preStock));
        ship.classList.toggle('is-pre', pre);
        ship.querySelector('[data-sr-s-ship-text]').textContent = pre ? ship.dataset.pre : ship.dataset.now;
      }

      const cta = s.querySelector('[data-sr-s-cta]');
      const label = s.querySelector('[data-sr-s-cta-label]');
      if (!lens.length) {
        cta.disabled = true;
        label.textContent = s.dataset.labelSoldout;
        return;
      }
      cta.disabled = picked < target;
      label.textContent = picked === 0
        ? s.dataset.labelChoose.replace('%C', target)
        : picked < target
          ? s.dataset.labelMore.replace('%C', target - picked)
          : s.dataset.labelAdd.replace('%T', formatMoney(boxTotal(full, pct, picked)));
    }

    async _addS() {
      const s = this.s;
      const cta = s.querySelector('[data-sr-s-cta]');
      const error = s.querySelector('[data-sr-s-error]');
      if (cta.disabled || cta.hasAttribute('aria-busy')) return;
      error.hidden = true;

      const plan = this.planSelect ? Number(this.planSelect.value) : null;
      const lines = this.sLens
        .map((el) => ({ id: Number(el.dataset.variantId), quantity: Number(el.dataset.count) }))
        .filter((line) => line.quantity > 0);
      if (plan) lines.forEach((line) => { line.selling_plan = plan; });
      const sets = lines.reduce((sum, line) => sum + line.quantity, 0);
      const items = [...lines];

      this.adding = true;
      cta.setAttribute('aria-busy', 'true');
      try {
        if (!this.returning) {
          const cart = await fetchCart();
          if (boxValue(cart, this.rule) + sets >= this.rule.giftMin) items.push(...missingGifts(cart, this.rule, this._chosenGift(), boxValue(cart, this.rule) + sets));
        }
        const cart = await addItems(items, this);
        if (cart && cart.items) this.setCart(cart, false);
        else this.render();
      } catch (err) {
        error.textContent = err.message;
        error.hidden = false;
      } finally {
        this.adding = false;
        cta.removeAttribute('aria-busy');
      }
    }

    // Arm C prices the chosen quantity at the tier the cart's box reaches with it, like the cart will.
    _renderC(subscribing) {
      const c = this.c;
      const unit = this.cPrice || this.price || 7500;
      const qty = this.cQty;
      const box = this.cartBox + qty;
      const pct = percentFor(box, this.rule);
      const perSet = boxUnit(unit, pct);
      const suffix = c.dataset.perSet || '';
      const total = subscribing ? perSet * qty : oneTimePrice(Array(qty).fill(unit));

      c.querySelectorAll('[data-sr-c-unit]').forEach((el) => {
        el.textContent = formatMoney(unit);
      });
      c.querySelector('[data-sr-c-now]').textContent = formatMoney(subscribing ? perSet : unit) + suffix;
      const was = c.querySelector('[data-sr-c-was]');
      was.textContent = formatMoney(unit) + suffix;
      was.hidden = !subscribing || perSet === unit;

      const hint = c.querySelector('[data-sr-c-hint]');
      hint.hidden = subscribing || !offer3();
      if (!hint.hidden) hint.textContent = threeForTwoHint(qty, this.dataset.kind);

      c.querySelector('[data-sr-c-qty-n]').textContent = String(qty);
      const cta = c.querySelector('[data-sr-c-cta]');
      cta.disabled = !this.cAvailable;
      c.querySelector('[data-sr-c-cta-label]').textContent = this.cAvailable
        ? (subscribing ? cta.dataset.labelSub : cta.dataset.labelOnce).replace('%T', formatMoney(total))
        : cta.dataset.labelSoldout;

      const boxLine = c.querySelector('[data-sr-c-box]');
      if (boxLine) {
        const worth = Math.round(((this.cartFull + unit * qty) * (100 - pct)) / 100);
        boxLine.textContent = `${box === 1 ? c.dataset.oneSet : t('cosnetic.js.sets', '{{ sets }} sæt', { sets: box })} · ${formatMoney(worth)}`;
      }

      const tiers = [...c.querySelectorAll('[data-sr-c-tier]')];
      const mins = tiers.map((el) => Number(el.dataset.srCTier));
      const reached = mins.reduce((last, min, i) => (box >= min ? i : last), -1);
      tiers.forEach((el, i) => {
        el.dataset.state = i < reached ? 'done' : i === reached ? 'active' : 'locked';
        const state = el.querySelector('[data-sr-c-state]');
        state.textContent = i === reached
          ? state.dataset.unlocked
          : i > reached
            ? t('cosnetic.js.add_sets_to_unlock', 'Tilføj {{ count }} sæt mere for at låse op', { count: mins[i] - box })
            : '';
      });
      const list = c.querySelector('.sr-c__tiers');
      if (list && mins.length) list.style.setProperty('--fill-n', String(railProgress(box, mins) / 100));
    }

    _onClick(event) {
      const target = event.target;

      const sSize = target.closest('[data-sr-s-size]');
      if (sSize) {
        this._pickSize(Number(sSize.dataset.srSSize));
        this.render();
        return;
      }

      const sChange = target.closest('[data-sr-s-change]');
      if (sChange) {
        const rows = this.s.querySelector('[data-sr-s-rows]');
        rows.hidden = !rows.hidden;
        sChange.setAttribute('aria-expanded', String(!rows.hidden));
        return;
      }

      const sStep = target.closest('[data-sr-s-step]');
      if (sStep) {
        const el = sStep.closest('[data-sr-s-len]');
        const delta = Number(sStep.dataset.srSStep);
        this._setS(el, Math.min(MAX_PER_LENGTH, Math.max(0, Number(el.dataset.count) + delta)));
        this.render();
        return;
      }

      if (target.closest('[data-sr-s-cta]')) {
        this._addS();
        return;
      }

      const cLen = target.closest('[data-sr-c-len]');
      if (cLen && !cLen.disabled) {
        setRadio(this.querySelectorAll('[data-sr-c-len]'), (chip) => chip === cLen);
        this.cVariant = Number(cLen.dataset.variantId);
        this.cPrice = Number(cLen.dataset.price);
        this.cAvailable = true;
        this.render();
        return;
      }

      const cQty = target.closest('[data-sr-c-qty]');
      if (cQty) {
        this.cQty = Math.min(MAX_PER_LENGTH, Math.max(1, this.cQty + Number(cQty.dataset.srCQty)));
        this.render();
        return;
      }

      if (target.closest('[data-sr-c-cta]')) {
        this._addC();
        return;
      }

      const modeTab = target.closest('[data-sr-mode]');
      if (modeTab) {
        this.mode = modeTab.dataset.srMode;
        this.render();
        document.dispatchEvent(new CustomEvent('sr:mode'));
        if (modeTab.hasAttribute('data-sr-close')) this.closeDrawer();
        return;
      }

      const onceVariant = target.closest('[data-sr-once-variant]');
      if (onceVariant && !onceVariant.disabled) {
        setRadio(this.querySelectorAll('[data-sr-once-variant]'), (chip) => chip === onceVariant);
        this.render();
        return;
      }

      const onceQty = target.closest('[data-sr-once-qty]');
      if (onceQty) {
        this.onceQty = Math.min(MAX_PER_LENGTH, Math.max(1, this.onceQty + Number(onceQty.dataset.srOnceQty)));
        this.render();
        return;
      }

      // Product page button: subscription opens the box builder, one-time adds straight to cart.
      if (target.closest('[data-sr-open]')) {
        if (this.mode === 'onetime') this._addOnce();
        else this.openDrawer();
        return;
      }

      if (target.closest('[data-sr-close]')) {
        this.closeDrawer();
        return;
      }

      const onceAddonStep = target.closest('[data-sr-once-addon-step]');
      if (onceAddonStep) {
        const addon = onceAddonStep.closest('[data-sr-once-addon]');
        const next = Math.min(MAX_PER_LENGTH, Math.max(0, (Number(addon.dataset.count) || 0) + Number(onceAddonStep.dataset.srOnceAddonStep)));
        addon.dataset.count = String(next);
        addon.classList.toggle('is-picked', next > 0);
        addon.querySelector('[data-sr-once-addon-count]').textContent = String(next);
        this.render();
        return;
      }

      const addonStep = target.closest('[data-sr-addon-step]');
      if (addonStep) {
        const addon = addonStep.closest('[data-sr-addon]');
        const next = Math.min(MAX_PER_LENGTH, Math.max(0, (Number(addon.dataset.count) || 0) + Number(addonStep.dataset.srAddonStep)));
        addon.dataset.count = String(next);
        addon.classList.toggle('is-picked', next > 0);
        addon.querySelector('[data-sr-addon-count]').textContent = String(next);
        this.render();
        return;
      }

      // One-click "Welcome Box": fill the builder with a ready-made box instead of
      // making the shopper assemble it. This is a starter-kit idea, built
      // out of the existing subscription products so it rides the Loop selling plan and
      // the discount ladder that are already in place -- no new SKU to stock or fulfil.
      const preset = target.closest('[data-sr-preset]');
      if (preset) {
        const sets = Number(preset.dataset.srPreset) || 8;
        const available = this.lens.filter((el) => !el.closest('.is-soldout'));
        if (available.length) {
          this.lens.forEach((el) => this._setCount(el, 0));
          // Spread the sets evenly across the lengths of THIS product, so the box is
          // usable straight away rather than eight of one length.
          const own = available.filter(
            (el) => Number(el.dataset.productId) === Number(this.dataset.productId));
          const pool = own.length ? own : available;
          for (let i = 0; i < sets; i++) {
            const el = pool[i % pool.length];
            this._setCount(el, (Number(el.dataset.count) || 0) + 1);
          }
          this.touched = true;
          this.render();
          const body = this.querySelector('.sr-drawer__body');
          if (body) body.scrollTo({ top: body.scrollHeight, behavior: 'smooth' });
        }
        return;
      }

      const step = target.closest('[data-sr-len-step]');
      if (step) {
        const len = step.closest('[data-sr-len]');
        this._setCount(len, (Number(len.dataset.count) || 0) + Number(step.dataset.srLenStep));
        this.touched = true;
        this.render();
        return;
      }

      const planChip = target.closest('[data-subrewards-plan-chip]');
      if (planChip) {
        setRadio(this.querySelectorAll('[data-subrewards-plan-chip]'), (chip) => chip === planChip);
        if (this.planSelect) this.planSelect.value = planChip.dataset.planId;
        return;
      }

      if (target.closest('[data-subrewards-add]')) {
        this._add();
        return;
      }

      const giftChoice = target.closest('[data-sr-gift-choice]');
      if (giftChoice) {
        const id = giftChoice.dataset.variantId;
        this.querySelectorAll('[data-sr-gift-choice]').forEach((el) => {
          const on = el.dataset.variantId === id;
          el.classList.toggle('is-selected', on);
          el.setAttribute('aria-pressed', String(on));
        });
        // Arm C: the later nail gift tier shows the design that was picked.
        const picked = giftChoice.querySelector('img');
        if (picked) this.querySelectorAll('[data-sr-c-gift-mirror] img').forEach((img) => {
          img.removeAttribute('srcset');
          img.src = picked.currentSrc || picked.src;
        });
        return;
      }

      const giftAdd = target.closest('[data-sr-gift-add]');
      if (giftAdd) {
        giftAdd.setAttribute('aria-busy', 'true');
        addMissingGifts(this.rule, this._chosenGift(), () => this.giftModal.close())
          .catch((error) => console.error('[subrewards]', error))
          .finally(() => giftAdd.removeAttribute('aria-busy'));
        return;
      }

      if (target.closest('[data-sr-gift-dismiss]')) {
        storage.set(GIFT_DISMISS_KEY, '1');
        this.giftModal.close();
      }
    }

    _chosenGift() {
      const chosen = this.querySelector('[data-sr-gift-choice].is-selected');
      return chosen ? Number(chosen.dataset.variantId) : null;
    }

    async _addOnce() {
      const button = this.querySelector('[data-sr-open]');
      const error = this.querySelector('[data-sr-pdp-error]');
      if (!this.onceAddons.length) return;
      error.hidden = true;
      button.setAttribute('aria-busy', 'true');
      try {
        const items = this.onceAddons.map((item) => ({ id: item.variantId, quantity: item.count }));
        await addItems(items, this);
        this.querySelectorAll('[data-sr-once-addon]').forEach((el) => {
          el.dataset.count = '0';
          el.classList.remove('is-picked');
          el.querySelector('[data-sr-once-addon-count]').textContent = '0';
        });
        this.render();
      } catch (err) {
        error.textContent = err.message;
        error.hidden = false;
      } finally {
        button.removeAttribute('aria-busy');
      }
    }

    async _addC() {
      const cta = this.c.querySelector('[data-sr-c-cta]');
      const error = this.c.querySelector('[data-sr-c-error]');
      if (!this.cAvailable || cta.hasAttribute('aria-busy')) return;
      error.hidden = true;

      const subscribing = this.mode === 'subscription';
      const plan = this.c.querySelector('[data-sr-c-plan]');
      const line = { id: this.cVariant, quantity: this.cQty };
      if (subscribing && plan) line.selling_plan = Number(plan.value);
      const items = [line];

      this.adding = true;
      cta.setAttribute('aria-busy', 'true');
      try {
        if (subscribing && !this.returning) {
          const cart = await fetchCart();
          if (boxValue(cart, this.rule) + this.cQty >= this.rule.giftMin) items.push(...missingGifts(cart, this.rule, this._chosenGift(), boxValue(cart, this.rule) + this.cQty));
        }
        const cart = await addItems(items, this);
        this.cQty = 1;
        if (cart && cart.items) this.setCart(cart, false);
        else this.render();
      } catch (err) {
        error.textContent = err.message;
        error.hidden = false;
      } finally {
        this.adding = false;
        cta.removeAttribute('aria-busy');
      }
    }

    async _add() {
      const picked = this.picked;
      if (!picked.length) return;
      this.errorEl.hidden = true;

      const subscribing = this.mode === 'subscription';
      const plan = subscribing && this.planSelect ? Number(this.planSelect.value) : null;
      const items = [...picked, ...(subscribing ? this.addons : [])].map((item) => {
        const line = { id: item.variantId, quantity: item.count };
        if (plan) line.selling_plan = plan;
        return line;
      });

      this.adding = true;
      this.cta.setAttribute('aria-busy', 'true');
      try {
        if (subscribing) {
          const adding = [...picked, ...this.addons].reduce((sum, item) => sum + item.count, 0);
          const cart = await fetchCart();
          if (!this.returning && boxValue(cart, this.rule) + adding >= this.rule.giftMin) items.push(...missingGifts(cart, this.rule, this._chosenGift(), boxValue(cart, this.rule) + adding));
        }
        await addItems(items, this, () => this.closeDrawer());
      } catch (error) {
        this.errorEl.textContent = error.message;
        this.errorEl.hidden = false;
      } finally {
        this.adding = false;
        this.cta.removeAttribute('aria-busy');
      }
    }
  }

  // Loop's plan description (edited in the Loop admin, shared by every product) still says
  // "SPAR 45%" / "Op til 45% rabat". The subscription offer tops out at 40%, so correct the
  // text wherever Loop renders it (accessory product pages that still use the Loop widget).
  const LOOP_TEXT_FIXES = [
    [/SPAR\s*45\s*%/gi, `SPAR OP TIL ${TOP_PCT}%`],
    [/(op til )?45\s*% rabat/gi, `Op til ${TOP_PCT}% rabat`],
    ...(SHIP_BOX > 0
      ? [[new RegExp(t('cosnetic.js.free_shipping_all', 'Gratis fragt på alle ordrer'), 'gi'),
          t('cosnetic.js.free_shipping_from', 'Gratis fragt fra {{ amount }}', { amount: formatMoney(SHIP_BOX) })]]
      // No free shipping (owner 2026-10-08: always charge): Loop's benefit line would be a false claim.
      : [[new RegExp(t('cosnetic.js.free_shipping_all', 'Gratis fragt på alle ordrer'), 'gi'),
          t('cosnetic.js.skip_cancel_any_time', 'Spring over eller opsig når som helst')]]),
  ];
  const fixLoopText = (root) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (!/45\s*%|alle ordrer/i.test(node.textContent)) continue;
      let text = node.textContent;
      LOOP_TEXT_FIXES.forEach(([pattern, replacement]) => { text = text.replace(pattern, replacement); });
      if (text !== node.textContent) node.textContent = text;
    }
  };
  const watchLoopWidgets = () => {
    document.querySelectorAll('.loop-widget-container').forEach((widget) => {
      if (widget.dataset.srTextFix) return;
      widget.dataset.srTextFix = '1';
      fixLoopText(widget);
      new MutationObserver(() => fixLoopText(widget)).observe(widget, { childList: true, subtree: true, characterData: true });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchLoopWidgets);
  else watchLoopWidgets();
  new MutationObserver(watchLoopWidgets).observe(document.documentElement, { childList: true, subtree: true });

  if (!window.customElements.get('subrewards-panel')) {
    window.customElements.define('subrewards-panel', SubrewardsPanel);
    // A visitor who accepts cookies is put in the split test after the panel rendered: draw it again.
    document.addEventListener('cx-x:change', () => document.querySelectorAll('subrewards-panel').forEach((panel) => panel.render && panel.render()));
  }

  // Arm C of the "buybox" test reads its box off the cart, so it follows Horizon's cart updates (drawer edits).
  // Only under cx-buybox-c: arms A and B keep the builder exactly as it was.
  import('@shopify/events')
    .then(({ StandardEvents }) => {
      document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
        if (!document.documentElement.classList.contains('cx-buybox-c')) return;
        const done = event.promise || (event.detail && event.detail.promise) || new Promise((r) => setTimeout(r, 1500));
        Promise.resolve(done).catch(() => {}).then(fetchCart).then((cart) => {
          document.querySelectorAll('subrewards-panel').forEach((panel) => panel.c && panel.setCart(cart, false));
        }).catch(() => {});
      });
    })
    .catch(() => {});

  // On load: clean up gifts left behind in a cart that no longer qualifies.
  fetchCart()
    .then((cart) => {
      lastCart = lastCart || cart;
      syncGifts(cart);
    })
    .catch(() => {});
})();
